import React from 'react';
import classnames from 'classnames';
import Image from 'next/image';
import { IImage, ILink } from 'utils/types';
import { setQueryParameters } from 'utils/setQueryParameters';
import { Tagline } from 'components/tagline/Tagline';
import { Warning } from 'components/catalog/Warning';
import { MegaButton } from 'components/button/MegaButton';
import { TextButton } from 'components/button/TextButton';

import s from './Product.module.scss';

type Props = {
  index?: number;
  tagline?: React.ReactNode;
  title: string;
  text?: string;
  image?: IImage;
  brandImage?: IImage;
  link?: ILink;
  linkText?: string;
  rated?: string;
  ratedLabel?: string;
  warning?: boolean;
  hasRems?: boolean;
  remsLink?: ILink;
  pdfLabel?: string;
  pdfLink?: ILink;
  externalAlert?: boolean;
  children: React.ReactNode;
};

export const Product = ({
  index,
  tagline,
  title,
  image,
  brandImage,
  link,
  warning,
  rated,
  ratedLabel = 'AB rated to',
  hasRems,
  remsLink,
  pdfLabel,
  pdfLink,
  externalAlert = true,
  children,
}: Props) => {
  const [alert, setAlert] = React.useState(false);
  const [externalLink, setExternalLink] = React.useState<ILink>(link || remsLink || pdfLink);
  const hasBrandImage = Boolean(brandImage?.url);

  const isLinkExternal =
    link?.href.includes('/files/') || /^((https?:)?\/\/|[0-9a-zA-Z]+:)/.test(link?.href || '');
  const isRemsLinkExternal =
    link?.href.includes('/files/') || /^((https?:)?\/\/|[0-9a-zA-Z]+:)/.test(remsLink?.href || '');

  React.useEffect(() => {
    console.log(externalLink);
  }, [externalLink]);

  return (
    <div className={s.product}>
      <div className={s.product__container}>
        <div className={s.product__content}>
          <div className={s.product__row}>
            <div className={s.product__column}>
              <div className={s.product__header}>
                {tagline && <Tagline index={index} text={tagline} />}
                {title && <h1 className={s.product__title}>{title}</h1>}
              </div>
            </div>
          </div>
          {(image || hasBrandImage) && (
            <div className={s.product__row}>
              <div className={s.product__column}>
                <div className={s.product__image}>
                  {hasBrandImage && (
                    <div className={classnames(s.product__brand, { [s.alone]: !image })}>
                      <Image
                        src={brandImage.thumbnail?.url ?? brandImage.url}
                        alt={brandImage.alt || ''}
                        width={brandImage.thumbnail?.width ?? brandImage.width}
                        height={brandImage.thumbnail?.height ?? brandImage.height}
                      />
                    </div>
                  )}
                  {image && (
                    <div className={s.product__frame}>
                      <picture>
                        <source
                          srcSet={setQueryParameters(
                            image.url as string,
                            'trim=color&trim-color=transparent'
                          )}
                        />
                        <img src={image.url} className={s.product__img} alt="" />
                      </picture>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className={s.product__row}>
            <div className={s.product__column}>
              <div className={s.product__info}>
                {warning && (
                  <div className={s.product__warning}>
                    This product contains a BOXED WARNING. See full prescribing information for this
                    product.
                  </div>
                )}
                {rated && (
                  <div className={s.product__rated}>
                    {ratedLabel && ratedLabel !== '' ? ratedLabel : 'AB rated to'}: {rated}
                    <a href="#footnote-*">*</a>
                  </div>
                )}
              </div>
              <div className={s.product__variants}>{children}</div>
              {link && (
                <div className={s.product__button}>
                  {warning && (
                    <MegaButton
                      onClick={
                        isLinkExternal && externalAlert
                          ? (event) => {
                              event.preventDefault();
                              setAlert(true);
                              setExternalLink(link);
                            }
                          : undefined
                      }
                      link={
                        isLinkExternal && !externalAlert ? { ...link, target: '_blank' } : undefined
                      }
                      ariaLabel="Prescribing info with boxed warning"
                    >
                      Prescribing info with boxed warning
                    </MegaButton>
                  )}
                  {!warning && (
                    <MegaButton
                      onClick={
                        isLinkExternal && externalAlert
                          ? (event) => {
                              event.preventDefault();
                              setAlert(true);
                              setExternalLink(link);
                            }
                          : undefined
                      }
                      link={
                        isLinkExternal && !externalAlert ? { ...link, target: '_blank' } : undefined
                      }
                      ariaLabel="Prescribing info"
                    >
                      Prescribing info
                    </MegaButton>
                  )}
                </div>
              )}
              {hasRems && remsLink && (
                <div className={s.product__button}>
                  <MegaButton
                    onClick={
                      isRemsLinkExternal && externalAlert
                        ? (event) => {
                            event.preventDefault();
                            setAlert(true);
                            setExternalLink(remsLink);
                          }
                        : undefined
                    }
                    link={
                      isRemsLinkExternal && !externalAlert
                        ? { ...remsLink, target: '_blank' }
                        : undefined
                    }
                    ariaLabel="Risk Evaluation and Mitigation Strategy (REMS)"
                  >
                    Risk Evaluation and Mitigation Strategy (REMS)
                  </MegaButton>
                </div>
              )}
              {!!pdfLink && (
                <div className={s.product__button}>
                  <MegaButton link={pdfLink} ariaLabel={pdfLabel}>
                    {pdfLabel || 'Download PDF'}
                  </MegaButton>
                </div>
              )}
              {rated && (
                <div className={s.product__footnote} role="doc-footnote" id="footnote-*">
                  *Trademarks (™) and registered trademarks (®) are property of their respective
                  companies and not the property of Alvogen.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {alert && externalAlert && (
        <Warning
          title="Please note"
          footer={
            <TextButton href={externalLink.href} target="_blank">
              Continue
            </TextButton>
          }
          onClose={() => setAlert(false)}
        >
          <div>
            <p>
              You are now leaving this page to visit a 3rd party website not operated by Alvogen.
              Please review the new sites’ Privacy Policy &amp; Terms and Conditions since they may
              differ from ours.
            </p>
          </div>
        </Warning>
      )}
    </div>
  );
};
